import { lazy, Suspense, useState } from 'react';
import { AppErrorBoundary } from '@/components/app-error-boundary';
import { useAuthQuery } from '@/services/auth';
import { hasWebPushAlertsSetup, isWebPushAlertsOn } from './gate';

// `lazy` keeps a rejected import for good, so a chunk that failed to load gets a fresh one, which the
// boundary renders (and so requests again) when the gate or the signed-in user next changes.
const lazyRuntime = () => lazy(() => import('./web-push-alerts-runtime'));

/**
 * Mounts the web push alerts runtime in the authenticated shell, only for a signed-in user the gate
 * lets in, or whose browser kept a setup the runtime must undo. Once mounted it stays, so a gate that
 * closes mid-session still tears down, and logout (the shell unmounting) still resets.
 */
export function WebPushAlertsMount() {
  const { data } = useAuthQuery();
  const account = data?.account;
  const user = data?.user;
  const [hadSetup] = useState(hasWebPushAlertsSetup);
  const [isMounted, setIsMounted] = useState(false);
  const [WebPushAlertsRuntime, setWebPushAlertsRuntime] = useState(lazyRuntime);
  const isOn = isWebPushAlertsOn(account, user);
  const isWanted = account != null && user != null && !account.is_masq && (hadSetup || isOn);
  if (isWanted && !isMounted) {
    setIsMounted(true);
  }
  if (!isWanted && !isMounted) {
    return null;
  }
  return (
    <AppErrorBoundary
      fallback={null}
      resetKeys={[isOn, account?.id, user?.id]}
      onError={() => setWebPushAlertsRuntime(lazyRuntime)}
    >
      <Suspense fallback={null}>
        <WebPushAlertsRuntime account={account} user={user} />
      </Suspense>
    </AppErrorBoundary>
  );
}
